import { z } from 'zod';
import { ehrAppointmentSchema } from './appointment';

/**
 * Patient demographics from EHR.
 */
export const ehrDemographicsSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().optional(),
  dateOfBirth: z.string(), // ISO 8601 date (YYYY-MM-DD)
  gender: z.enum(['male', 'female', 'other', 'unknown']).optional(),
  preferredLanguage: z.string().optional(),
  ethnicity: z.string().optional(),
  race: z.string().optional(),
  maritalStatus: z.string().optional(),
});

export type EhrDemographics = z.infer<typeof ehrDemographicsSchema>;

/**
 * Address from EHR.
 */
export const ehrAddressSchema = z.object({
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  use: z.enum(['home', 'work', 'temp', 'billing']).optional(),
});

export type EhrAddress = z.infer<typeof ehrAddressSchema>;

/**
 * Contact info from EHR.
 */
export const ehrContactSchema = z.object({
  phones: z
    .array(
      z.object({
        number: z.string(),
        use: z.enum(['home', 'work', 'mobile']).optional(),
        primary: z.boolean().optional(),
      })
    )
    .optional(),
  emails: z
    .array(
      z.object({
        address: z.string(),
        use: z.enum(['home', 'work']).optional(),
        primary: z.boolean().optional(),
      })
    )
    .optional(),
  addresses: z.array(ehrAddressSchema).optional(),
});

export type EhrContact = z.infer<typeof ehrContactSchema>;

/**
 * Insurance coverage from EHR.
 * Aligned with FHIR Coverage resource (https://build.fhir.org/coverage.html).
 */
export const ehrInsuranceCoverageSchema = z.object({
  // FHIR Coverage.status
  status: z.enum(['active', 'cancelled', 'draft']).optional(),

  // FHIR Coverage.insurer
  payerId: z.string().optional(),
  payerName: z.string().optional(),

  // FHIR Coverage.subscriber + subscriberId
  subscriberId: z.string().optional(),
  subscriberLastName: z.string().optional(),
  subscriberFirstName: z.string().optional(),
  subscriberMiddleName: z.string().optional(),

  // FHIR Coverage.relationship
  relationship: z.string().optional(), // "self", "spouse", "child", "other"

  // FHIR Coverage.identifier (beneficiary's member ID)
  memberId: z.string().optional(),

  // FHIR Coverage.class[group]
  groupNumber: z.string().optional(),
  groupName: z.string().optional(),

  // FHIR Coverage.class[plan]
  planName: z.string().optional(),
  planType: z.string().optional(), // e.g., "PPO", "HMO"

  // FHIR Coverage.period
  coverageStart: z.string().optional(), // ISO 8601 date
  coverageEnd: z.string().optional(), // ISO 8601 date

  // FHIR Coverage.network
  network: z.string().optional(),

  // FHIR Coverage.costToBeneficiary
  copay: z.number().optional(),
  deductible: z.number().optional(),
  deductibleMet: z.number().optional(),

  // FHIR Coverage.order (1=primary, 2=secondary, 3=tertiary)
  order: z.number().optional(),
});

export type EhrInsuranceCoverage = z.infer<typeof ehrInsuranceCoverageSchema>;

/**
 * Patient insurance from EHR.
 */
export const ehrInsuranceSchema = z.object({
  primary: ehrInsuranceCoverageSchema.optional(),
  secondary: ehrInsuranceCoverageSchema.optional(),
  tertiary: ehrInsuranceCoverageSchema.optional(),
});

export type EhrInsurance = z.infer<typeof ehrInsuranceSchema>;

/**
 * Care team member from EHR.
 */
export const ehrCareTeamMemberSchema = z.object({
  ehrProviderId: z.string().optional(),
  name: z.string(),
  role: z.string().optional(), // e.g., "Primary Care Provider", "Specialist"
  specialty: z.string().optional(),
  phone: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().optional(),
  npi: z.string().optional(),
  facility: z.string().optional(),
});

export type EhrCareTeamMember = z.infer<typeof ehrCareTeamMemberSchema>;

/**
 * Patient care team from EHR.
 */
export const ehrCareTeamSchema = z.object({
  primaryCareProvider: ehrCareTeamMemberSchema.optional(),
  members: z.array(ehrCareTeamMemberSchema),
});

export type EhrCareTeam = z.infer<typeof ehrCareTeamSchema>;

/**
 * Medication from EHR.
 */
export const ehrMedicationSchema = z.object({
  name: z.string(),
  genericName: z.string().optional(),
  dosage: z.string().optional(),
  form: z.string().optional(), // e.g., "tablet", "capsule", "injection"
  frequency: z.string().optional(),
  route: z.string().optional(), // e.g., "oral", "topical", "injection"
  startDate: z.string().optional(), // ISO 8601 date
  endDate: z.string().optional(), // ISO 8601 date
  prescribedDate: z.string().optional(), // ISO 8601 date
  status: z.enum(['active', 'stopped', 'completed', 'on-hold']).optional(),
  prescribedBy: z.string().optional(),
  instructions: z.string().optional(),
  refillsRemaining: z.number().optional(),
  daysSupply: z.number().optional(),
  rxNumber: z.string().optional(),
});

export type EhrMedication = z.infer<typeof ehrMedicationSchema>;

/**
 * Patient summary returned from EHR lookup.
 */
export const ehrPatientSummarySchema = z.object({
  ehrPatientId: z.string(),
  mrn: z.string().optional(),
  demographics: ehrDemographicsSchema,
  contact: ehrContactSchema.optional(),
});

export type EhrPatientSummary = z.infer<typeof ehrPatientSummarySchema>;

/**
 * Full patient data from EHR.
 * appointments: today + future + last previous appointment.
 * The consumer treats this as a complete set for transactional replace.
 */
export const ehrPatientDataSchema = z.object({
  ehrPatientId: z.string(),
  mrn: z.string().optional(),
  demographics: ehrDemographicsSchema,
  contact: ehrContactSchema.optional(),
  insurance: ehrInsuranceSchema.optional(),
  careTeam: ehrCareTeamSchema.optional(),
  medications: z.array(ehrMedicationSchema).optional(),
  appointments: z.array(ehrAppointmentSchema).optional(),
});

export type EhrPatientData = z.infer<typeof ehrPatientDataSchema>;
